import { issuancesApi } from './issuanceApi';
import {
  PROPERTY_CLASSIFICATION,
  PROPERTY_CLASSIFICATION_LABEL,
  type PropertyClassification,
} from './issuanceTypes';

/**
 * The ICS / PAR a custody issuance prints — fetched, kept, saved and printed.
 *
 * **The blob is RE-TYPED to `application/pdf` on arrival**, the rule `useDocumentPreview`
 * sets out for every file this product frames: an object URL is same-origin with the page that
 * made it, so its media type is what decides which parser the browser hands the bytes to. Pinned
 * here, a payload that was secretly something else reaches the PDF viewer, which declines it,
 * rather than the HTML parser. The server sends `nosniff` and an attachment, and this does not
 * undo either.
 */
const PDF = 'application/pdf';

/** `Download ICS`, `Print PAR` — the form's own abbreviation, which is what the slip calls itself. */
export function issuanceFormLabel(form: PropertyClassification): string {
  return PROPERTY_CLASSIFICATION_LABEL[form];
}

/** The form's own printed title — what the document calls itself at its head. */
export function issuanceFormTitle(form: PropertyClassification): string {
  return form === PROPERTY_CLASSIFICATION.par
    ? 'Property Acknowledgment Receipt'
    : 'Inventory Custodian Slip';
}

/**
 * Why a slip's form cannot be produced, or null when it can.
 *
 * A deleted slip prints nothing — the server refuses it too, with the same sentence, because a
 * signed form for a record the register has withdrawn is a document about nothing.
 */
export function issuanceFormDisabledReason(record: { isActive: boolean }): string | null {
  return record.isActive ? null : 'This issuance is deleted, so it has no form to print. Restore it first.';
}

export async function fetchIssuanceForm(
  id: string,
  signal?: AbortSignal,
): Promise<{ blob: Blob; fileName: string | null }> {
  const { blob, fileName } = await issuancesApi.form(id, signal);

  return { blob: new Blob([blob], { type: PDF }), fileName };
}

/**
 * Hands the PDF to the browser as a file.
 *
 * **An object URL, not an anchor pointed at the endpoint** — a navigation carries no bearer
 * token. Revoked at once: it is dead outside this tab anyway.
 */
export function saveIssuanceForm(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = window.document.createElement('a');

  anchor.href = url;
  anchor.download = fileName;
  window.document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  URL.revokeObjectURL(url);
}

/**
 * Opens the browser's print dialog over the PDF.
 *
 * **A hidden frame, printed once it has loaded** — the browser's own PDF viewer does the
 * printing, so the page prints exactly what the preview and the download show. A browser that
 * refuses to print a framed PDF gets it in a new tab instead, where its viewer's own Print is
 * one click away.
 *
 * The frame and its URL are cleared after a minute rather than on `afterprint`, which the
 * frame's PDF viewer does not reliably raise; a print dialog left open that long has already
 * spooled the document.
 */
export function printIssuanceForm(blob: Blob): void {
  const url = URL.createObjectURL(new Blob([blob], { type: PDF }));
  const frame = window.document.createElement('iframe');

  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';

  const cleanUp = () => {
    window.setTimeout(() => {
      frame.remove();
      URL.revokeObjectURL(url);
    }, 60_000);
  };

  frame.onload = () => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } catch {
      window.open(url, '_blank', 'noopener');
    }

    cleanUp();
  };

  frame.src = url;
  window.document.body.appendChild(frame);
}
